// Inline detail panel — shows when a row is expanded. Notes + due
// date + reminder toggle + priority + tag.
//
// Important: text fields (notes textarea, future title editor) keep
// their value in LOCAL state and only sync to the server on blur.
// Per-keystroke RPCs created a race where the in-flight response
// would land mid-typing and rewind the textarea — visible as "space
// doesn't work" and laggy typing. Clicks (due preset, reminder,
// importance star, tag) still patch immediately because they're
// single-event changes with no race.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import type { TodoItem, TodoTag } from "../../lib/rpc";
import {
  buildPreset, combineDateTime, dateOnly, fromInputValue, timeOnly, toInputValue,
} from "./helpers";

interface TodoDetailProps {
  todo: TodoItem;
  onUpdate: (patch: Partial<TodoItem>) => void;
  /** User's managed tag list. Detail panel falls back to a #personal
   *  literal when this is empty (server should always seed defaults,
   *  but the fallback keeps the dropdown from going blank during the
   *  initial fetch). */
  tagOptions: TodoTag[];
}

const PRESETS = [
  { label: "Today evening",    offset: { h: 18 } },
  { label: "Tomorrow morning", offset: { d: 1, h: 9 } },
  { label: "In an hour",       offset: { mins: 60 } },
  { label: "This Friday",      offset: { day: 5, h: 17 } },
  { label: "Next week",        offset: { d: 7, h: 10 } },
] as const;

// Recurrence presets — chips offered in the picker, each mapped to a
// canonical RRULE generated from the item's due date. Same shape as the
// calendar's RECUR_PRESETS so the visual language matches across surfaces.
type RecurPreset = "none" | "daily" | "weekly" | "monthly" | "yearly" | "custom";
const RECUR_PRESETS: { id: RecurPreset; label: string }[] = [
  { id: "none",    label: "Doesn't repeat" },
  { id: "daily",   label: "Daily" },
  { id: "weekly",  label: "Weekly" },
  { id: "monthly", label: "Monthly" },
  { id: "yearly",  label: "Yearly" },
  { id: "custom",  label: "Custom…" },
];

const RRULE_DAY_NAMES = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

function rruleForPreset(id: RecurPreset, anchor: Date): string | null {
  if (id === "none" || id === "custom") return null;
  if (id === "daily")   return "FREQ=DAILY";
  if (id === "weekly")  return `FREQ=WEEKLY;BYDAY=${RRULE_DAY_NAMES[anchor.getDay()]}`;
  if (id === "monthly") return `FREQ=MONTHLY;BYMONTHDAY=${anchor.getDate()}`;
  if (id === "yearly")  return "FREQ=YEARLY";
  return null;
}

function presetForRrule(rrule: string | null, anchor: Date): RecurPreset {
  if (!rrule) return "none";
  if (rrule === "FREQ=DAILY") return "daily";
  if (rrule === `FREQ=WEEKLY;BYDAY=${RRULE_DAY_NAMES[anchor.getDay()]}`) return "weekly";
  if (rrule === `FREQ=MONTHLY;BYMONTHDAY=${anchor.getDate()}`) return "monthly";
  if (rrule === "FREQ=YEARLY") return "yearly";
  return "custom";
}

export function TodoDetail({ todo, onUpdate, tagOptions }: TodoDetailProps) {
  const tag = todo.tags?.[0] || "personal";

  // Local copy of the body so the textarea isn't a fully controlled
  // input fighting in-flight RPC responses. Sync from prop when the
  // server-side body changes from somewhere we DIDN'T initiate (e.g.
  // another tab editing the same todo) — done by re-keying on todo.id
  // + last persisted body length, which jumps when an external write
  // lands. For typical local typing, we never re-sync mid-edit.
  const [bodyDraft, setBodyDraft] = useState(todo.body || "");
  // Same local-until-blur pattern for the title so renaming doesn't race
  // in-flight RPC responses (see the file-top note).
  const [titleDraft, setTitleDraft] = useState(todo.title || "");
  useEffect(() => {
    setBodyDraft(todo.body || "");
    setTitleDraft(todo.title || "");
  // Re-sync ONLY when the row identity changes (collapsed → expand a
  // different row), not on every render of the same row.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todo.id]);

  const commitTitle = () => {
    const t = titleDraft.trim();
    if (!t) { setTitleDraft(todo.title || ""); return; } // empty → revert (title is required)
    if (t !== todo.title) onUpdate({ title: t });
  };

  const setDueFromInput = (val: string) => {
    onUpdate({ dueAt: val ? fromInputValue(val) : null });
  };

  // Split-picker handlers. Date input fires first → if no time is set,
  // combineDateTime defaults to 5pm so the resulting ISO is sensible.
  // Time input picks up the existing date or "today" if there's no
  // dueAt yet.
  const setDueDate = (dateStr: string) => {
    if (!dateStr) { onUpdate({ dueAt: null }); return; }
    const next = combineDateTime(dateStr, timeOnly(todo.dueAt));
    onUpdate({ dueAt: next });
  };
  const setDueTime = (timeStr: string) => {
    const existingDate = dateOnly(todo.dueAt);
    if (!existingDate) {
      // Time without a date — assume today.
      const today = dateOnly(new Date().toISOString());
      onUpdate({ dueAt: combineDateTime(today, timeStr) });
      return;
    }
    onUpdate({ dueAt: combineDateTime(existingDate, timeStr) });
  };

  return (
    <div className="todo-detail" onClick={(e) => e.stopPropagation()}>
      <div className="td-field">
        <label className="td-label">Title</label>
        <input
          className="td-title-input"
          value={titleDraft}
          maxLength={500}
          placeholder="What needs doing?"
          onInput={(e) => setTitleDraft((e.currentTarget as HTMLInputElement).value)}
          onBlur={commitTitle}
          onKeyDown={(e) => { if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur(); }}
        />
      </div>

      <div className="td-field">
        <label className="td-label">Notes</label>
        <textarea
          className="td-textarea"
          rows={3}
          placeholder="Anything you'd want to remember when you start this…"
          value={bodyDraft}
          onInput={(e) => setBodyDraft((e.currentTarget as HTMLTextAreaElement).value)}
          onBlur={() => {
            if (bodyDraft !== (todo.body || "")) onUpdate({ body: bodyDraft });
          }}
        />
      </div>

      <div className="td-field">
        <label className="td-label">Due</label>
        <div className="td-due-row">
          {/* Split pickers — native OS date picker, then native time
              picker. Each input fires its own native dropdown on tap,
              which is what the user expected (vs the cramped time
              spinner inside datetime-local). */}
          <input
            type="date"
            className="td-due-input td-due-date"
            value={dateOnly(todo.dueAt)}
            onInput={(e) => setDueDate((e.currentTarget as HTMLInputElement).value)}
          />
          <input
            type="time"
            className="td-due-input td-due-time"
            value={timeOnly(todo.dueAt)}
            onInput={(e) => setDueTime((e.currentTarget as HTMLInputElement).value)}
            title="Time"
          />
          <button
            className="td-notify"
            data-on={!!todo.alarmEnabled && !!todo.dueAt}
            disabled={!todo.dueAt}
            title={todo.dueAt ? (todo.alarmEnabled ? "Reminder on" : "Reminder off") : "Set a due time first"}
            onClick={() => onUpdate({ alarmEnabled: !todo.alarmEnabled })}
            type="button"
          >
            <Icon name="alarm" size={13} />
            {todo.alarmEnabled && todo.dueAt ? "Will remind you" : "Remind me"}
          </button>
          {todo.dueAt && (
            <button
              className="td-due-clear"
              type="button"
              onClick={() => onUpdate({ dueAt: null, alarmEnabled: false })}
            >
              clear
            </button>
          )}
        </div>
        <div className="td-presets">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              className="td-preset"
              type="button"
              onClick={() => setDueFromInput(buildPreset(p.offset))}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="td-field">
        <label className="td-label">Flags</label>
        <div className="td-meta-row">
          <button
            className="td-pri"
            data-on={todo.priority === "high"}
            onClick={() => onUpdate({ priority: todo.priority === "high" ? "med" : "high" })}
            type="button"
          >
            <Icon name={todo.priority === "high" ? "star-fill" : "star"} size={12} />
            {todo.priority === "high" ? "Important" : "Mark important"}
          </button>
          <select
            className="td-tag-select"
            value={tag}
            onChange={(e) => onUpdate({ tags: [(e.currentTarget as HTMLSelectElement).value] })}
          >
            {/* Keep the current tag selectable even if it's no longer in
                the user's set (e.g. the tag was deleted between renders).
                Otherwise the <select> would fall back to the first option
                and silently rewrite the todo on the next change event. */}
            {tagOptions.find((t) => t.name === tag) ? null : (
              <option key={tag} value={tag}>#{tag}</option>
            )}
            {tagOptions.map((t) => (
              <option key={t.id} value={t.name}>#{t.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="td-field">
        <label className="td-label">Repeats</label>
        {todo.dueAt ? (
          <>
            <div className="td-recur-chips">
              {(() => {
                const anchor = new Date(todo.dueAt);
                const currentPreset = presetForRrule(todo.rrule, anchor);
                return RECUR_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="td-recur-chip"
                    data-active={currentPreset === p.id}
                    onClick={() => {
                      if (p.id === "custom") {
                        if (currentPreset !== "custom") onUpdate({ rrule: todo.rrule || "" });
                      } else {
                        onUpdate({ rrule: rruleForPreset(p.id, anchor) });
                      }
                    }}
                  >
                    {p.label}
                  </button>
                ));
              })()}
            </div>
            {presetForRrule(todo.rrule, new Date(todo.dueAt)) === "custom" && (
              <input
                className="td-recur-custom"
                placeholder="FREQ=WEEKLY;BYDAY=MO,WE,FR"
                value={todo.rrule || ""}
                onBlur={(e) => onUpdate({ rrule: (e.currentTarget as HTMLInputElement).value || null })}
                onInput={(e) => {/* local-only until blur; same anti-thrash pattern as the body field */}}
              />
            )}
            <div className="td-recur-hint">
              When you check this off, the next occurrence spawns automatically.
            </div>
          </>
        ) : (
          <div className="td-recur-hint td-recur-hint-disabled">
            Set a due date first — recurrence needs an anchor.
          </div>
        )}
      </div>
    </div>
  );
}
